import type { CanonicalStage } from '@capture/shared';

export type DeepLinkSurface = 'form' | 'processing' | 'admin';

export interface DeepLink {
  /** Why an operator would click this — appears as the row's lead text. */
  label: string;
  /** Destination surface — drives the asymmetric prefix in the renderer. */
  surface: DeepLinkSurface;
  /**
   * Display name of the destination tab or page.
   * Required when status === 'available' (renderer pairs it with the chevron).
   * Optional for coming-soon (renderer shows the literal "coming soon" instead).
   */
  destination?: string;
  /** Link target. Required when status === 'available'. */
  href?: (jobTypeId: string) => string;
  /** Hidden destinations roadmap — render as muted, disabled, no chevron. */
  status: 'available' | 'coming-soon';
}

export const STAGE_DEEP_LINKS: Partial<Record<CanonicalStage, DeepLink[]>> = {
  'import': [
    {
      // 2026-06-29 — Header moved to Job Config (rework Task 8).
      label: 'Header schema',
      surface: 'processing',
      destination: 'Header tab',
      href: (id) => `/admin/job-types/${id}?tab=header`,
      status: 'available',
    },
    {
      label: 'Intake requirements',
      surface: 'form',
      status: 'coming-soon',
    },
  ],
  'form-recognition': [
    {
      label: 'Recognition hints, samples, threshold',
      surface: 'form',
      destination: 'Recognition tab',
      href: (id) => `/admin/forms/${id}?tab=recognition`,
      status: 'available',
    },
  ],
  'ocr': [
    {
      // 2026-06-29 — OCR profiles moved to Form Config (rework Task 9).
      label: 'OCR profiles',
      surface: 'form',
      destination: 'OCR Profiles',
      href: (id) => `/admin/forms/${id}?tab=ocr-profiles`,
      status: 'available',
    },
    {
      // 2026-06-29 — Zones moved to Form Config (rework Task 9).
      label: 'Zones',
      surface: 'form',
      destination: 'Zones',
      href: (id) => `/admin/forms/${id}?tab=zones`,
      status: 'available',
    },
    {
      label: 'Calibration',
      surface: 'processing',
      destination: 'Calibration',
      href: (id) => `/admin/job-types/${id}?tab=calibration`,
      status: 'available',
    },
    {
      label: 'Handwriting',
      surface: 'processing',
      destination: 'Handwriting',
      href: (id) => `/admin/job-types/${id}?tab=handwriting`,
      status: 'available',
    },
  ],
  'post-ocr': [
    {
      label: 'Field transforms',
      surface: 'form',
      destination: 'Fields tab',
      href: (id) => `/admin/forms/${id}?tab=fields`,
      status: 'available',
    },
    {
      label: 'Custom transform library',
      surface: 'admin',
      status: 'coming-soon',
    },
  ],
  'key-entry': [
    {
      label: 'Field validation rules',
      surface: 'form',
      destination: 'Fields tab',
      href: (id) => `/admin/forms/${id}?tab=fields`,
      status: 'available',
    },
    {
      label: 'Roles & permissions',
      surface: 'admin',
      destination: 'Roles',
      href: () => '/admin/roles',
      status: 'available',
    },
  ],
  'verification': [
    {
      label: 'Field validation rules',
      surface: 'form',
      destination: 'Fields tab',
      href: (id) => `/admin/forms/${id}?tab=fields`,
      status: 'available',
    },
    {
      // F-052 — QC sampling lives on the Job Config page's Workflow tab
      // (the step's Routing Condition), not in Form Config.
      label: 'QC sampling (Hold % for QC)',
      surface: 'processing',
      destination: 'Workflow',
      href: (id) => `/admin/job-types/${id}?tab=workflow`,
      status: 'available',
    },
  ],
  'qa': [
    {
      label: 'Field validation rules',
      surface: 'form',
      destination: 'Fields tab',
      href: (id) => `/admin/forms/${id}?tab=fields`,
      status: 'available',
    },
    {
      // F-052 — QC sampling lives on the Job Config page's Workflow tab
      // (the step's Routing Condition), not in Form Config.
      label: 'QC sampling (Hold % for QC)',
      surface: 'processing',
      destination: 'Workflow',
      href: (id) => `/admin/job-types/${id}?tab=workflow`,
      status: 'available',
    },
  ],
  'pre-export': [
    {
      label: 'Compute fields',
      surface: 'form',
      destination: 'Fields tab',
      href: (id) => `/admin/forms/${id}?tab=fields`,
      status: 'available',
    },
    {
      label: 'Batch validation gates',
      surface: 'form',
      status: 'coming-soon',
    },
  ],
  'export': [
    {
      label: 'Per-form field mappings',
      surface: 'form',
      destination: 'Export mapping tab',
      href: (id) => `/admin/forms/${id}?tab=export-mapping`,
      status: 'available',
    },
  ],
};
